import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { Loader } from '@citrus/loader';
import { Layout, Header } from '@citrus/layout';
import { PrimaryButton, SecondaryButton } from '@citrus/button';
import { Drawer } from '@citrus/drawer';
import CitrusFormViewer from "../Common/Components/citrusFormViewer/citrusFormViewer";
import ConfirmationModal from '../Common/Components/confirmation/confirmationModal';
import FlashMessage from '../Common/Components/flashMessage/flashMessage.container';
import { getAnswerCount, getQuestionCount, isNullOrEmptyGuid } from '../Common/Services/utilities';
import { FormViewerActionTakenDisplays, TalentFormFormTypes } from './formViewer.constants';
import OptOutConfirmationModal from "./optOutConfirmation/optOutConfirmation.container";
import moment from 'moment';
import './formViewer.less';
import { isNullOrEmpty } from '@pcty/utils';
import * as checkIllustration from '../../Images/check-illustration.svg';
import * as noActionNeeded from '../../Images/noaction-needed.svg';
import * as VideoApiService from '../Common/Services/VideoApiService';


const autoSaveFrequencyMs = 30000;

class FormViewer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isDrawerOpen: false,
      lastInstanceGuid: undefined,
      lastInstanceParticipantGuid: undefined,
      lastForeignIdValue: undefined,
      isOptOutButtonDisabled: this.isOptionEnabled("isOpenSurvey"),
    }

    this.autoSaveIntervalId = undefined;
    this.caption = this.isOptionEnabled("useSurveyLabels") ? "survey" : "form";
    this.captionUpper = this.isOptionEnabled("useSurveyLabels") ? "Survey" : "Form";
  }

  UNSAFE_componentWillMount() {
    this.initializeFormViewer();
  }

  componentDidMount() {
    if (!this.isOptionEnabled("readOnly")) {
      this.autoSaveIntervalId = setInterval(() => this.saveAsDraftIfDirty(), autoSaveFrequencyMs);
    }
}

  componentDidUpdate(prevProps, prevState) {
    this.enableOptOutButtonForOpenSurveysThatWerePreviouslySavedAsDraft(prevProps);

    const isEmbedded = this.isOptionEnabled("embeddedViewerMode");
    if (isEmbedded) {
      this.initializeFormViewer();
    }
  }

  enableOptOutButtonForOpenSurveysThatWerePreviouslySavedAsDraft(prevProps) {
    if (this.props.formInstance.statusDescription === "In Progress" &&
        this.props.formInstance.statusDescription !== prevProps.formInstance.statusDescription  &&
        this.isOptionEnabled("isOpenSurvey")) {
      this.setState({isOptOutButtonDisabled: false});
    }
  }

  componentWillUnmount() {
    if (!this.isOptionEnabled("readOnly")) {
      // MUST clearInterval() or the interval/auto-save thread will keep going and going and going!
      clearInterval(this.autoSaveIntervalId);
    }
  }

  isOptionEnabled(option) {
    let enabled = (this.props.options && this.props.options[option] === true) ? true : false;
    return enabled;
  }

  getOptionValue(option) {
    let value = (this.props.options && this.props.options[option]) ? this.props.options[option] : undefined;
    return value;
  }

  initializeFormViewer() {
    if (this.props.params) {
      let instanceGuid = this.props.params.instanceGuid;
      let instanceParticipantGuid = this.props.params.instanceParticipantGuid;
      let companyId = this.props.params.companyId;
      let employeeId = this.props.params.employeeId;
      let employeeName = this.getOptionValue("employeeName");
      let isPublicSurvey = this.isOptionEnabled("isPublicSurvey");
      let isReadOnly = this.isOptionEnabled("readOnly");
      let moduleId = this.getOptionValue("moduleId");
      let formTypeId = this.getOptionValue("formTypeId");
      let foreignIdValue = this.getOptionValue("foreignIdValue");
      let createNewParticipantRecord = this.isOptionEnabled("createNewParticipantRecord");

      if (this.isOptionEnabled("fetchFromInstanceGuid")) {
        if (this.props.formInstance && this.props.formInstance.formInstanceGuid !== instanceGuid && this.state.lastInstanceGuid !== instanceGuid) {
          this.props.getLaunchedFormByInstanceOrParticipantGuid(companyId, employeeId, employeeName, instanceGuid, undefined, isPublicSurvey, isReadOnly, createNewParticipantRecord, formTypeId, foreignIdValue);
          // make sure we don't get into an infinite loop with failed requests!
          this.setState({ lastInstanceGuid: instanceGuid });
        }
      }
      else if (this.isOptionEnabled("fetchFromForeignIdValue") && foreignIdValue && formTypeId) {
        if (this.props.formInstance && this.props.formInstance.foreignId !== foreignIdValue && this.state.lastForeignIdValue !== foreignIdValue) {
          this.props.getLaunchedFormByInstanceOrParticipantGuid(companyId, employeeId, employeeName, undefined, undefined, isPublicSurvey, isReadOnly, createNewParticipantRecord, formTypeId, foreignIdValue);
          // make sure we don't get into an infinite loop with failed requests!
          this.setState({ lastForeignIdValue: foreignIdValue });
        }
      }
      else {
        if (this.props.formInstance && this.props.formInstance.formInstanceParticipantGuid !== instanceParticipantGuid && this.state.lastInstanceParticipantGuid !== instanceParticipantGuid) {
          this.props.getLaunchedFormByInstanceOrParticipantGuid(companyId, employeeId, employeeName, instanceGuid, instanceParticipantGuid, isPublicSurvey, isReadOnly, createNewParticipantRecord, formTypeId, foreignIdValue, moduleId);
          // make sure we don't get into an infinite loop with failed requests!
          this.setState({ lastInstanceParticipantGuid: instanceParticipantGuid });
        }
      }

      if (this.isOptionEnabled("drawerViewerMode")) {
        this.setState({ isDrawerOpen: true });
      }
    }
  }

  closeDrawer() {
    this.setState({ isDrawerOpen: false });
    var evt = new CustomEvent('PctyTalentFormViewerDrawerClosed', { detail: this.props.formInstance });
    window.dispatchEvent(evt);
  }

  saveAsDraftIfDirty() {
    let formInstance = this.props.formInstance;
    let displayMode = this.getFormMode(formInstance);
    let isPublicSurvey = this.isOptionEnabled("isPublicSurvey");

    if (displayMode === "edit" && this.props.isFormModified && !this.props.isSaving) {
      formInstance.answerCount = getAnswerCount(JSON.parse(formInstance.answers));
      formInstance.questionCount = getQuestionCount(formInstance.configuration, true);
      this.props.saveDraftLaunchedForm(formInstance, true, true, isPublicSurvey);
      this.setState({
        isOptOutButtonDisabled: false
      });
    }
  }

  getFormMode(formInstance, override) {
    // if displayMode hasn't been set, figure it out!
    let formInstanceMode = 'display';
    if (formInstance && formInstance.configuration) {
      // make the formInstance read-only if it's been completed, rescinded, or closed
      if (this.isOptionEnabled("readOnly") || formInstance.completedDate || formInstance.isClosed || formInstance.statusDescription === 'Rescinded' || formInstance.statusDescription === 'Completed') {
        formInstanceMode = 'display';
      }
      else {
        formInstanceMode = 'edit';
      }

      if (override && !formInstance.isClosed) {
        formInstanceMode = 'edit';
      }
    }

    return formInstanceMode;
  }

  onFormModified(result) {
    let formInstance = this.props.formInstance;
    let displayMode = this.getFormMode(formInstance);
    let answers = JSON.stringify(result);

    if (displayMode === "edit" && answers !== formInstance.answers) {
      this.props.setFormModified(answers);
    }
  }

  handleCompleteFormClick = (allVisibleQuestionsCount = undefined, formInstance = undefined) => {
    const isPostProcessingOptionSet = this.isOptionEnabled("requirePreProcessingBeforeComplete");

    if(isPostProcessingOptionSet) {
      this.doPostProcessingCompleteFormProcess(allVisibleQuestionsCount, formInstance);
    } else {
      this.doStandardCompleteFormProcess(allVisibleQuestionsCount, formInstance);
    }
  }

  doStandardCompleteFormProcess(allVisibleQuestionsCount, optionalFormInstance) {
    let formInstance = this.props.formInstance;

    // Use optional formInstance if provided. Otherwise, continue with formInstance from props
    if(optionalFormInstance != undefined)
      formInstance = optionalFormInstance;

    let rawUnansweredCount = allVisibleQuestionsCount - formInstance.answerCount;
    
    if(allVisibleQuestionsCount != undefined) {
      this.props.updateFormUnansweredCount(rawUnansweredCount);
    }

    let isPublicSurvey = this.isOptionEnabled("isPublicSurvey");

    // cannot use the prop because it hasn't completed it's cycle yet!
    if (rawUnansweredCount > 0) {
      this.forceUpdate();
      this.props.showConfirmationModal(formInstance);
    }
    else {
      this.props.completeLaunchedForm(formInstance, true, isPublicSurvey);

      if (this.isOptionEnabled("drawerViewerMode")) {
        this.closeDrawer();
      }
    }
  };

  doPostProcessingCompleteFormProcess(allVisibleQuestionsCount, optionalFormInstance) {
    let formInstance = this.props.formInstance;

    // Use optional formInstance if provided. Otherwise, continue with formInstance from props
    if(optionalFormInstance != undefined)
      formInstance = optionalFormInstance;

    let rawUnansweredCount = allVisibleQuestionsCount - formInstance.answerCount;

    if(allVisibleQuestionsCount != undefined) {
      this.props.updateFormUnansweredCount(rawUnansweredCount);
    }

    let isPublicSurvey = this.isOptionEnabled("isPublicSurvey");

    // cannot use the prop because it hasn't completed it's cycle yet!
    if (rawUnansweredCount > 0) {
      this.forceUpdate();
      this.props.showConfirmationModal(formInstance);
    }
    else {
      this.props.saveDraftWithPostProcessing(formInstance, isPublicSurvey);

      if (this.isOptionEnabled("drawerViewerMode")) {
        this.closeDrawer();
      }
    }
  };


  hasValidReturnUrl() {
    const query = new URLSearchParams(window.location.search);
    const isValidReturnUrl = (query && query.has('returnUrl') && (query.get('returnUrl').length > 5));
    return isValidReturnUrl;
  }

  handleCancelFormClick = () => {
    if (this.props.isFormModified) {
      this.props.formInstanceCancelRequested();
      this.forceUpdate();
      this.props.showConfirmationModal()
    }
    else {
      this.cancelForm();
    }
  }

  cancelForm() {
    if(!this.hasValidReturnUrl()) {
      this.props.resetState();
    }

    var evt = new CustomEvent('PctyTalentFormViewerCancelled', { detail: this.props.formInstance });
    window.dispatchEvent(evt);

    if (this.isOptionEnabled("drawerViewerMode")) {
      this.closeDrawer();
    }
  }

  handleBackClick() {
    if(!this.hasValidReturnUrl()) {
      this.props.resetState();
    }

    var evt = new CustomEvent('PctyTalentFormViewerGoBack', { detail: this.props.params });
    window.dispatchEvent(evt);

    if (this.isOptionEnabled("drawerViewerMode")) {
      this.closeDrawer();
    }
  }

  handleDeclineFormClick = () => {
    this.props.showOptOutConfirmationModal(this.props.formInstance);
  }

  renderFormNotAvailable() {
    let defaultContent = `The ${this.caption} is no longer available for you to complete.`;
    if (this.isOptionEnabled('readOnly')) {
      defaultContent = `The ${this.caption} is no longer available.`;
    }

    let formInstanceNotAvailableContent = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.${this.caption}NotAvailableContent`, defaultMessage: defaultContent });

    const preventConfidentialViewing = (this.props.formInstance.isConfidential && this.isOptionEnabled('preventViewingIfConfidential'));
    if (preventConfidentialViewing) {
      defaultContent = `This is a confidential ${this.caption}. You do not have permission to view the responses to this ${this.caption}.`;
      formInstanceNotAvailableContent = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.${this.caption}NotAvailableConfidentialContent`, defaultMessage: defaultContent });
    }

    const multipleResponsesAllowed = this.isOptionEnabled("multipleResponsesAllowed");
    const formAvailable = !preventConfidentialViewing && (multipleResponsesAllowed || !this.props.isFormNotAvailable);

    return (!formAvailable && <div className="pcty-row">
      <div className="pcty-col-xs-12 pcty-padding-all pcty-type-centeralign action-completed-container" style={{ minHeight: '250px' }}>
        <img src={noActionNeeded} alt="Form Not Available" />
        <br />
        <br />
        <h3>{formInstanceNotAvailableContent}</h3>
      </div>
    </div>);
  }

  renderConfirmationModals() {
    let isPublicSurvey = this.isOptionEnabled("isPublicSurvey");

    if (this.props.unansweredCount > 0 && !this.props.isFormCancelRequested) {
      let title = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.incomplete${this.captionUpper}Title`, defaultMessage: `Incomplete ${this.captionUpper}` });
      let body = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.incomplete${this.captionUpper}Content`, defaultMessage: `You have UNANSWERED_COUNT unanswered question(s). Are you sure you want to submit this ${this.caption}?` });
      body = body.replace("UNANSWERED_COUNT", this.props.unansweredCount);
      let confirm = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.submit${this.captionUpper}`, defaultMessage: `Submit ${this.captionUpper}` });
      let dismiss = this.context.intl.formatMessage({ id: `${this.captionUpper}Common.cancel`, defaultMessage: "Cancel" });

      return <ConfirmationModal
        headerText={title}
        body={body}
        confirmText={confirm}
        dismissText={dismiss}
        onConfirmCallback={(formInstance) => this.handleCompleteFormClick(undefined, formInstance)}
      />
    }
    else {
      let title = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.unsavedChangesTitle`, defaultMessage: "Unsaved Changes" });
      let body = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.unsavedChangesContent`, defaultMessage: "You have changes that will be lost if you leave without saving." });
      let confirm = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.discardChanges`, defaultMessage: "Discard Changes" });
      let dismiss = this.context.intl.formatMessage({ id: `${this.captionUpper}Common.cancel`, defaultMessage: "Cancel" });

      return <ConfirmationModal
        headerText={title}
        body={body}
        confirmText={confirm}
        dismissText={dismiss}
        onConfirmCallback={() => this.cancelForm()}
        onDismissCallback={() => this.props.formInstanceCancelCancelled()}
      />
    }
  }

  onGetViewVideoToken(companyId, videoId, isPublicForm) {
    if (!isNullOrEmpty(videoId)) {
     return VideoApiService.getSingleViewToken(companyId, videoId, isPublicForm);
    }
  };

  renderFormQuestions() {
    let hideOptOut = this.isOptionEnabled("hideOptOut") || this.isOptionEnabled("isPublicSurvey");
    let allowSaveDraft = !this.isOptionEnabled("isPublicSurvey");
    let allowCancel = !this.isOptionEnabled("hideCancel");
    let displayMode = this.getFormMode(this.props.formInstance, this.isOptionEnabled("forceInProgress"));
    let hideAnswers = !this.isOptionEnabled("showNonCompleteAnswers") && displayMode === 'display' && this.props.formInstance.statusDescription !== 'Completed';
    let isPublicSurvey = this.isOptionEnabled("isPublicSurvey");
    let companyId = this.props.params.companyId;
    const preventConfidentialViewing = (this.props.formInstance.isConfidential && this.isOptionEnabled('preventViewingIfConfidential'));

    const usersLanguage = () => {
      let language = 'en';

      if (this.getOptionValue("preferredLocale")) {
        language = this.getOptionValue("preferredLocale");
      }

      return language;
    };

    const getCaptions = () => {
      let captions = [];
      captions.push({id: "SurveyDetails.optOut", value: this.context.intl.formatMessage({ id: `SurveyDetails.optOut`, defaultMessage: "Opt Out" })});
      captions.push({id: "SurveyDetails.submitSurvey", value: this.context.intl.formatMessage({ id: `SurveyDetails.submitSurvey`, defaultMessage: "Submit Survey" })});
      captions.push({id: "SurveyDetails.complete", value: this.context.intl.formatMessage({ id: `SurveyDetails.complete`, defaultMessage: "Complete" })});
      captions.push({id: "SurveyDetails.saveDraft", value: this.context.intl.formatMessage({ id: `SurveyDetails.saveDraft`, defaultMessage: "Save Draft" })});
      captions.push({id: "SurveyDetails.nextPage", value: this.context.intl.formatMessage({ id: `SurveyDetails.nextPage`, defaultMessage: "Next Page" })});
      captions.push({id: "SurveyDetails.previousPage", value: this.context.intl.formatMessage({ id: `SurveyDetails.previousPage`, defaultMessage: "Previous Page" })});
      captions.push({id: "SurveyDetails.otherValueRequired", value: this.context.intl.formatMessage({ id: `SurveyDetails.otherValueRequired`, defaultMessage: "Please enter the other value." })});
      captions.push({id: "SurveyDetails.answerRequired", value: this.context.intl.formatMessage({ id: `SurveyDetails.answerRequired`, defaultMessage: "Please answer the question." })});
      captions.push({id: "SurveyDetails.answerAllRequired", value: this.context.intl.formatMessage({ id: `SurveyDetails.answerAllRequired`, defaultMessage: "Please answer all required questions." })});
      captions.push({id: "SurveyDetails.requiredQuestionIndicator", value: this.context.intl.formatMessage({ id: `SurveyDetails.requiredQuestionIndicator`, defaultMessage: "Required question" })});
      captions.push({id: "SurveyDetails.selectValue", value: this.context.intl.formatMessage({ id: `SurveyDetails.selectValue`, defaultMessage: "Select..." })});
      captions.push({id: "SurveyDetails.enterText", value: this.context.intl.formatMessage({ id: `SurveyDetails.enterText`, defaultMessage: "Enter text" })});
      captions.push({id: "SurveyDetails.enterValue", value: this.context.intl.formatMessage({ id: `SurveyDetails.enterValue`, defaultMessage: "Enter value" })});
      captions.push({id: "SurveyDetails.otherText", value: this.context.intl.formatMessage({ id: `SurveyDetails.otherText`, defaultMessage: "Other (describe)" })});
      captions.push({id: "SurveyDetails.cancel", value: this.context.intl.formatMessage({ id: `SurveyCommon.cancel`, defaultMessage: "Cancel" })});
      captions.push({id: "SurveyWidgets.ConstantSum.allocated", value: this.context.intl.formatMessage({ id: `SurveyWidgets.ConstantSum.allocated`, defaultMessage: "allocated" })});
      captions.push({id: "SurveyWidgets.ConstantSum.total", value: this.context.intl.formatMessage({ id: `SurveyWidgets.ConstantSum.total`, defaultMessage: "Total" })});
      captions.push({id: "SurveyWidgets.ConstantSum.totalCannotExceed", value: this.context.intl.formatMessage({ id: `SurveyWidgets.ConstantSum.totalCannotExceed`, defaultMessage: "The total cannot exceed TARGET_SUM." })});
      captions.push({id: "SurveyWidgets.ConstantSum.sumMustEqual", value: this.context.intl.formatMessage({ id: `SurveyWidgets.ConstantSum.sumMustEqual`, defaultMessage: "The sum of all values must equal TARGET_SUM." })});
      captions.push({id: "SurveyWidgets.ConstantSum.minimumInputRange", value: this.context.intl.formatMessage({ id: `SurveyWidgets.ConstantSum.minimumInputRange`, defaultMessage: "Values must have a minimum of MINIMUM_INPUT." })});
      captions.push({id: "SurveyWidgets.ConstantSum.maximumInputRange", value: this.context.intl.formatMessage({ id: `SurveyWidgets.ConstantSum.maximumInputRange`, defaultMessage: "Values may have a maximum of MAXIMUM_INPUT." })});
      captions.push({id: "SurveyWidgets.ConstantSum.minAndMaxInputRange", value: this.context.intl.formatMessage({ id: `SurveyWidgets.ConstantSum.minAndMaxInputRange`, defaultMessage: "Values must be between MINIMUM_INPUT and MAXIMUM_INPUT." })});
      captions.push({id: "SurveyWidgets.StackRank.confirmOrder", value: this.context.intl.formatMessage({ id: `SurveyWidgets.StackRank.confirmOrder`, defaultMessage: "Confirm order after ranking all answers" })});

      return captions;
    };

    const multipleResponsesAllowed = this.getOptionValue("multipleResponsesAllowed");
    const formAvailable = !preventConfidentialViewing && (multipleResponsesAllowed || !this.props.isFormNotAvailable);
    const videoFeatureEnabled = this.isOptionEnabled("allowVideoFeature");
    return (formAvailable && (!videoFeatureEnabled || this.props.isVideoV2ConfigurationLoaded) &&
      this.props.formInstanceActionTakenDisplay === FormViewerActionTakenDisplays.NoDisplay &&
      <div className="pcty-row">
        <div className="pcty-col-xs-12 pcty-padding-vertical citrus-form-viewer-container" style={{ minHeight: '150px' }}>
          <CitrusFormViewer
            id="citrus-form-viewer"
            configuration={this.props.formInstance.configuration}
            answers={hideAnswers ? null : this.props.formInstance.answers}
            formName={this.props.formInstance.templateName || ''}
            locale={usersLanguage()}
            captions={getCaptions()}
            displayMode={displayMode}
            allowSaveDraftForm={allowSaveDraft}
            allowDeclineForm={!hideOptOut}
            allowCancelForm={allowCancel}
            onFormModified={(result) => this.onFormModified(result)}
            onFormSaveDraft={() => this.props.saveDraftLaunchedForm(this.props.formInstance, true, false, isPublicSurvey)}
            onFormComplete={(allVisibleQuestionsCount) => this.handleCompleteFormClick(allVisibleQuestionsCount)}
            onFormCancel={() => this.handleCancelFormClick()}
            onFormDecline={() => this.handleDeclineFormClick()}
            onGetViewVideoToken={(videoId) => this.onGetViewVideoToken(companyId, videoId, isPublicSurvey)}
            isOptOutButtonDisabled={this.state.isOptOutButtonDisabled}
            videoV2Configuration={this.props.videoV2Configuration}
          />
        </div>
      </div>);
  }

  renderAutoSaveText() {
    let lastSaved = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.lastSaved`, defaultMessage: "Last saved" });

    return (this.props.lastAutoSave && this.props.formInstanceActionTakenDisplay === FormViewerActionTakenDisplays.NoDisplay &&
      <div className="pcty-row">
        <div className="pcty-col-xs-12 pcty-type-rightalign" style={{ marginBottom: '15px' }}>
          <em>{lastSaved}: <span>{moment(this.props.lastAutoSave).format('M/D/YYYY h:mm:ss a')}</span></em>
        </div>
      </div>
    );
  }

  renderConfidentialBanner() {
    let confidential = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.confidential`, defaultMessage: `All responses to this ${this.caption} are confidential.` });

    if (this.props.formInstance.isConfidential)
      return (<div className="confidential-banner">
        <div className="pcty-inset-banner pcty-information">
          <div className="pcty-banner-column pcty-icon-column">
            <i className="pcty-banner-icon"></i>
          </div>
          <div className="pcty-banner-column pcty-banner-message">
            <p className="pcty-type-semibold">{confidential}</p>
          </div>
        </div>
      </div>
      );
  }

  renderNonConfidentialBanner() {
    let nonConfidentialText = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.confidential`, defaultMessage: `All responses to this ${this.caption} are not confidential; your name will be shown to the requestor.` });

    if (this.props.formInstanceActionTakenDisplay == FormViewerActionTakenDisplays.NoDisplay && !this.props.formInstance.isConfidential && this.props.options.canShowNonConfidentialBanner)
      return (<div className="confidential-banner">
        <div className="pcty-inset-banner pcty-information">
          <div className="pcty-banner-column pcty-icon-column">
            <i className="pcty-banner-icon"></i>
          </div>
          <div className="pcty-banner-column pcty-banner-message">
            <p className="pcty-type-semibold">{nonConfidentialText}</p>
          </div>
        </div>
      </div>
      );
  }

  renderStatusAndDueDateBanner() {
    const renderDueDate = () => {
      if (!this.props.formInstance.dueDate || this.props.formInstance.dueDate.indexOf("0001-01-01") > -1)
        return null;

      let todayStart = moment().startOf('day');
      let dueDate = moment(this.props.formInstance.dueDate);
      let dayDiff = todayStart.diff(dueDate.startOf('day'), 'days');

      if (dayDiff > 0)
        return (
          <span>
            <i className="icon-attention form-overdue" />
            <span className="form-overdue">{dueDate.format('M/D/YYYY')}</span>
          </span>
        );
      return (<span className="form-due-label">{dueDate.format('M/D/YYYY')}</span>);
    }

    const renderStatus = () => {
      let completedOn = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.${this.caption}CompletedOn`, defaultMessage: `This ${this.caption} was completed on` });
      let due = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.due`, defaultMessage: "Due" });

      if (this.props.formInstance.completedDate) return (
        <span className="pcty-banner-actions">
          {completedOn} {moment(this.props.formInstance.completedDate).format('M/D/YYYY')}.
        </span>
      );

      return (this.props.formInstance.dueDate &&
        <span className="pcty-banner-actions">
          <span className="form-due-label">{due}: </span>
          {renderDueDate()}
        </span>);
    }

    return ((!this.isOptionEnabled("hideDueDate") &&
    (this.props.formInstance.dueDate || this.props.formInstance.completedDate)) &&
      <div className="pcty-padding-none form-reminder">
        <div className="pcty-inset-banner status-banner">
          <div className="pcty-banner-column pcty-banner-message">
            {renderStatus()}
          </div>
        </div>
      </div>);
  }

  renderActionCompletedView() {
    let formInstanceCompletedContent = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.${this.caption}CompletedContent`, defaultMessage: `You have successfully completed this ${this.caption}!` });
    let formInstanceSavedDraftContent = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.${this.caption}SavedDraftContent`, defaultMessage: `You have successfully saved a draft of this ${this.caption}!` });
    let formInstanceOptedOutContent = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.${this.caption}OptedOutContent`, defaultMessage: `You have successfully opted-out of this ${this.caption}!` });

    return (this.props.formInstanceActionTakenDisplay !== FormViewerActionTakenDisplays.NoDisplay &&
      <div className="pcty-row">
        <div className="pcty-col-xs-12 pcty-padding-all pcty-type-centeralign action-completed-container" style={{ minHeight: '250px' }}>
          <img src={checkIllustration} alt="No forms" />
          <br />
          <br />
          {
            this.props.formInstanceActionTakenDisplay === FormViewerActionTakenDisplays.Completed &&
            <h3>{formInstanceCompletedContent}</h3>
          }
          {
            this.props.formInstanceActionTakenDisplay === FormViewerActionTakenDisplays.SaveAsDraft &&
            <h3>{formInstanceSavedDraftContent}</h3>
          }
          {
            this.props.formInstanceActionTakenDisplay === FormViewerActionTakenDisplays.OptedOut &&
            <h3>{formInstanceOptedOutContent}</h3>
          }
        </div>
      </div>);
  }

  renderOptOutConfirmationModal() {
    let isPublicSurvey = this.isOptionEnabled("isPublicSurvey");

    let title = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.optOutTitle`, defaultMessage: `Opt Out of ${this.captionUpper}` });
    let body = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.optOutContent`, defaultMessage: `If you opt out you will not have the opportunity to complete the ${this.caption}. Opting out will remove the ${this.caption} from your Active ${this.caption} list.` });
    let confirm = this.context.intl.formatMessage({ id: `${this.captionUpper}Details.optOut`, defaultMessage: "Opt Out" });
    let dismiss = this.context.intl.formatMessage({ id: `${this.captionUpper}Common.cancel`, defaultMessage: "Cancel" });

    return <OptOutConfirmationModal
      headerText={title}
      body={body}
      confirmText={confirm}
      dismissText={dismiss}
      onConfirmCallback={(formInstance) => this.props.declineLaunchedForm(formInstance, true, isPublicSurvey)}
    />
  }

  renderPublicFooter() {
    if (!this.isOptionEnabled("isPublicSurvey")) return null;

    let logoImgUrl = this.getOptionValue("paylocityCdnLogoUrl");
    return (<div className="pcty-row">
      <div className="pcty-col-xs-12 footer-logo">
        <div className="powered-by-paylo">
          <p style={{ fontSize: '12px' }}>Powered by
          <img src={logoImgUrl} style={{ marginLeft: '7px', top: '6px', position: 'relative' }} height="20px" alt="paylocity logo" /></p>
          <br /><br />
          <a target="_blank" rel="noopener noreferrer nofollow" href="https://www.paylocity.com/privacy/" style={{ textDecoration: 'none' }}>Privacy Policy</a>
        </div>
      </div>
    </div>);
  }

  renderAsDrawerViewer() {
    if (!this.isOptionEnabled("drawerViewerMode")) return null;
    const closeButtonCaption = this.getOptionValue("drawerCloseButtonCaption") || 'Close';

    let headerTitle = !this.props.isLoading ?
                        this.isOptionEnabled("showFeedbackTitle") && this.getOptionValue("assignedToName") ?
                          `${this.getOptionValue("feedbackName") || this.props.formInstance.templateName}: ${this.getOptionValue("assignedToName")} Response` :
                          (this.props.formInstance.templateName || `Invalid ${this.captionUpper}`) :
                          '';
    if (this.props.options.customHeader) {
      headerTitle = this.props.options.customHeader;
    }

    let view = null;

    if (!this.props.isLoading) {
      view = <div className="form-detail">
        {!this.isOptionEnabled("hideBanners") ? (<div className="pcty-row">
          <div className="pcty-col-xs-12 pcty-padding-vertical">
            {this.renderStatusAndDueDateBanner()}
            {this.renderConfidentialBanner()}
          </div>
        </div>) : () => {}}
        {this.renderFormQuestions()}
        {this.renderAutoSaveText()}
        {this.renderActionCompletedView()}
        {this.renderFormNotAvailable()}
        {this.renderPublicFooter()}
      </div>
    }

    const renderActionArea = () =>
      <React.Fragment>
        <SecondaryButton automationId="viewer-drawer-close"
          onClick={() => this.closeDrawer()}>{closeButtonCaption}</SecondaryButton>
      </React.Fragment>;

    return (
      <Drawer id="form-viewer-drawer"
        onClose={() => this.handleCancelFormClick()}
        open={this.state.isDrawerOpen}
        position="right"
        size="large">
        {!this.isOptionEnabled("hideTitleBar") &&
          <Header id="form-viewer-drawer-title"
            title={headerTitle}
            renderActionArea={renderActionArea} />
        }
        <Loader loading={this.props.isLoading} />
        <FlashMessage />
        {this.renderConfirmationModals()}
        {this.renderOptOutConfirmationModal()}
        {view}
      </Drawer>
    )
  };

  renderAsEmbeddedViewer() {
    if (!this.isOptionEnabled("embeddedViewerMode")) return null;

    let view = null;

    if (!this.props.isLoading) {
      view = <React.Fragment>
        {this.renderFormQuestions()}
        {this.renderFormNotAvailable()}
      </React.Fragment>
    }

    return (
      <React.Fragment>
        <Loader loading={this.props.isLoading} global={true} />
        <FlashMessage />
        {view}
      </React.Fragment>
    );
  }

  render() {
    if (this.isOptionEnabled("drawerViewerMode")) return this.renderAsDrawerViewer();
    if (this.isOptionEnabled("embeddedViewerMode")) return this.renderAsEmbeddedViewer();

    const isHiddenViewerMode = this.isOptionEnabled("hiddenViewerMode");  // for exporting pdfs
    let view = null;

    const renderHeader = () => {
      if (this.isOptionEnabled("hideTitleBar")) return null;

      const hideViewerBackNavigation = this.isOptionEnabled("hideViewerBackNavigation");

      let headerTitle = !this.props.isLoading ? (this.props.formInstance.templateName || `Invalid ${this.captionUpper}`) : '';
      if (this.props.options.customHeader) {
        headerTitle = this.props.options.customHeader;
      }

      return hideViewerBackNavigation ? <Header title={headerTitle} /> : <Header title={headerTitle} onBack={() => this.handleBackClick()} />;
    }

    if (!this.props.isLoading) {
      view = <React.Fragment>
        <div className="pcty-row">
          {this.renderStatusAndDueDateBanner()}
          {this.renderConfidentialBanner()}
          {this.renderNonConfidentialBanner()}
        </div>

        {this.renderFormQuestions()}
        {this.renderAutoSaveText()}
        {this.renderActionCompletedView()}
        {this.renderFormNotAvailable()}
        {this.renderPublicFooter()}
      </React.Fragment>
    }

    let formClasses = isHiddenViewerMode ? "form-detail hidden-form-detail" : "form-detail";
    return (
      <React.Fragment>
        <Loader loading={this.props.isLoading} global={true} />
        <FlashMessage />
        {this.renderConfirmationModals()}
        {this.renderOptOutConfirmationModal()}

        <Layout renderHeader={renderHeader} className={formClasses}>
          {view}
        </Layout>
      </React.Fragment>
    );
  }
}

FormViewer.propTypes = {
  params: PropTypes.object.isRequired,
  options: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  updateFormUnansweredCount: PropTypes.func.isRequired,
  unansweredCount: PropTypes.number.isRequired,
  formInstance: PropTypes.object.isRequired,
  getLaunchedFormByInstanceOrParticipantGuid: PropTypes.func.isRequired,
  saveDraftLaunchedForm: PropTypes.func.isRequired,
  completeLaunchedForm: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  setFormModified: PropTypes.func.isRequired,
  isFormModified: PropTypes.bool.isRequired,
  formInstanceCancelRequested: PropTypes.func.isRequired,
  formInstanceCancelCancelled: PropTypes.func.isRequired,
  isFormCancelRequested: PropTypes.bool.isRequired,
  showConfirmationModal: PropTypes.func.isRequired,
  declineLaunchedForm: PropTypes.func.isRequired,
  isFormNotAvailable: PropTypes.bool.isRequired,
  isSessionExpiring: PropTypes.bool.isRequired,
  clearSessionTimeoutExpiring: PropTypes.func.isRequired,
  formInstanceActionTakenDisplay: PropTypes.number.isRequired,
  lastAutoSave: PropTypes.object,
  videoConfiguration: PropTypes.object.isRequired,
  isVideoV2ConfigurationLoaded: PropTypes.bool.isRequired,
  videoV2Configuration: PropTypes.object.isRequired
};


FormViewer.contextTypes = {
  intl: PropTypes.object.isRequired
};

export default FormViewer;